import React, { useState, useEffect } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Rating,
  IconButton,
  Button,
  Avatar,
  Tooltip,
  Badge,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Gavel,
  Timer,
  Visibility,
  Share,
  LocationOn,
  Store,
  TrendingUp,
  PhotoCamera,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from '../../contexts/TranslationContext';
import BidPlacementPopup from './BidPlacementPopup';
import BidPlacementHistoryPopup from './BidPlacementHistoryPopup';
import { AuctionService } from '../../services/auctionService';
import { supabase } from '../../lib/supabase';

interface AuctionCardProps {
  product: {
    id: string;
    name: string;
    price: number;
    originalPrice?: number;
    image: string;
    rating: number;
    reviewCount: number;
    category: string;
    sellerId?: string;
    seller: {
      name: string;
      avatar: string;
      location: string;
    };
    isNew?: boolean;
    isFeatured?: boolean;
    discount?: number;
    description?: string;
    tags?: string[];
    // Auction-specific fields
    auction?: {
      id: string;
      end_time: string;
      current_bid: number;
      bid_increment: number;
      status: string;
      reserve_price?: number;
      bid_count: number;
      highest_bidder?: {
        name: string;
        avatar?: string;
      };
    };
  };
  onAddToCart?: (productId: string) => void;
  onToggleFavorite?: (productId: string) => void;
  onViewDetails?: (productId: string, navigationState?: any) => void;
  onShare?: (productId: string) => void;
  isFavorite?: boolean;
  isOwnProduct?: boolean;
  variant?: 'default' | 'compact' | 'detailed';
  showSeller?: boolean;
  navigationState?: {
    currentPage: number;
    filters: any;
    searchQuery: string;
    sortBy: string;
    viewMode: string;
  };
}

const AuctionCard: React.FC<AuctionCardProps> = ({
  product,
  onAddToCart,
  onToggleFavorite,
  onViewDetails,
  onShare,
  isFavorite = false,
  isOwnProduct = false,
  variant = 'default',
  showSeller = true,
  navigationState,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    total: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });

  const [isEnded, setIsEnded] = useState(false);
  const [bidPopupOpen, setBidPopupOpen] = useState(false);
  const [bidHistoryOpen, setBidHistoryOpen] = useState(false);

  // Track freshest auction values for display on the card
  const [latestAuction, setLatestAuction] = useState<{
    id: string;
    current_bid: number;
    bid_count: number;
  } | null>(null);

  const auction = latestAuction || product.auction || null;
  const currentBid = auction?.current_bid ?? product.price;
  const bidCount = auction?.bid_count ?? (product.auction?.bid_count || 0);
  const reservePrice = product.auction?.reserve_price;
  const hasReservePrice = typeof reservePrice === 'number' && reservePrice > 0;
  const imageCount = (() => {
    const p: any = product as any;
    const listA = Array.isArray(p.images) ? p.images : [];
    const listB = Array.isArray(p.backblaze_images) ? p.backblaze_images : [];
    const listC = Array.isArray(p.image_urls) ? p.image_urls : [];
    const listD = Array.isArray(p.imageUrls) ? p.imageUrls : [];
    const main = typeof p.image === 'string' ? p.image.trim() : '';

    const merged = [...listA, ...listB, ...listC, ...listD]
      .map((u) => String(u || '').trim())
      .filter((u) => u.length > 0);
    const unique = Array.from(new Set(merged));
    if (unique.length > 0) return unique.length;
    return main.length > 0 ? 1 : 0;
  })();
  const categoryLabel = String((product as any).category || '').trim();
  const showCategory = categoryLabel.length > 0 && categoryLabel.toLowerCase() !== 'unknown';
  const showPrice = typeof currentBid === 'number' ? currentBid > 0 : Boolean(currentBid);

  // Calculate time remaining
  useEffect(() => {
    if (!product.auction?.end_time) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const endTime = new Date(product.auction!.end_time).getTime();
      const difference = endTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeRemaining({ days, hours, minutes, seconds, total: difference });
        setIsEnded(false);
      } else {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });
        setIsEnded(true);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [product.auction?.end_time]);

  // Initial refresh of highest bid and bid count on mount
  useEffect(() => {
    let isMounted = true;
    const refresh = async () => {
      if (!product.auction?.id) return;
      try {
        const bids = await AuctionService.getAuctionBids(product.auction.id);
        const highest = bids && bids.length > 0 ? bids[0].bid_amount : (product.auction.current_bid ?? product.price);
        if (!isMounted) return;
        setLatestAuction({
          id: product.auction.id,
          current_bid: highest,
          bid_count: bids?.length ?? (product.auction.bid_count || 0),
        });
      } catch (_) {
        // ignore background refresh failures on list cards
      }
    };
    refresh();
    return () => {
      isMounted = false;
    };
  }, [product.auction?.id]);

  // Realtime: keep card up to date as bids come in
  useEffect(() => {
    if (!product.auction?.id) return;
    const channel = supabase
      .channel(`auction-card-bids-${product.auction.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'auction_bids', filter: `auction_id=eq.${product.auction.id}` },
        async () => {
          try {
            const bids = await AuctionService.getAuctionBids(product.auction!.id);
            const highest = bids && bids.length > 0 ? bids[0].bid_amount : (product.auction!.current_bid ?? product.price);
            setLatestAuction({
              id: product.auction!.id,
              current_bid: highest,
              bid_count: bids?.length ?? (product.auction!.bid_count || 0),
            });
          } catch (_) {
            // ignore realtime refresh errors
          }
        }
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch {}
    };
  }, [product.auction?.id]);

  const formatTimeRemaining = () => {
    if (isEnded) return t('auctionCard.status.ended');
    
    const { days, hours, minutes, seconds } = timeRemaining;
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getTimeColor = () => {
    if (isEnded) return '#f44336';
    if (timeRemaining.total < 3600000) return '#ff9800'; // Less than 1 hour - orange
    if (timeRemaining.total < 86400000) return '#ffc107'; // Less than 1 day - yellow
    return '#4caf50'; // More than 1 day - green
  };

  const getProgressPercentage = () => {
    if (!product.auction) return 0;
    
    const now = new Date().getTime();
    const startTime = new Date(product.auction.end_time).getTime() - (7 * 24 * 60 * 60 * 1000); // Assuming 7 days duration
    const endTime = new Date(product.auction.end_time).getTime();
    
    if (now < startTime) return 0;
    if (now > endTime) return 100;
    
    return ((now - startTime) / (endTime - startTime)) * 100;
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart?.(product.id);
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(product.id);
  };

  const handleViewDetails = () => {
    onViewDetails?.(product.id, navigationState);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(product.id);
  };

  const handleBid = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBidPopupOpen(true);
  };

  const handleViewBids = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBidHistoryOpen(true);
  };

  const handlePlaceBid = async (bidAmount: number) => {
    if (!product.auction?.id) {
      throw new Error('Auction not available for this product');
    }

    // Create the bid in Supabase via AuctionService
    await AuctionService.createBid({
      auction_id: product.auction.id,
      bid_amount: bidAmount,
    });
  };

  if (variant === 'compact') {
    return (
      <>
        <motion.div
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.99 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            borderRadius: 1,
            boxShadow: 'none',
            border: '2px solid #ff6b35',
            background: 'linear-gradient(135deg, #fff5f0 0%, #ffffff 100%)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(255, 107, 53, 0.2)',
              borderColor: '#ff5722',
            },
            transition: 'all 0.2s ease',
            p: 2,
            mb: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
          onClick={handleViewDetails}
        >
          {/* Auction Badge */}
          <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
            <Chip
              icon={<Gavel sx={{ fontSize: 18, color: 'white' }} />}
              label={t('auctionCard.badge.auction')}
              size="small"
              sx={{
                height: 30,
                px: 0.5,
                fontSize: '0.8rem',
                fontWeight: 900,
                letterSpacing: '0.2px',
                color: 'white',
                background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                boxShadow: '0 12px 26px rgba(15, 23, 42, 0.20)',
                border: '1px solid rgba(255,255,255,0.35)',
                backdropFilter: 'blur(10px)',
                '& .MuiChip-icon': { ml: 0.5 },
                '& .MuiChip-label': { px: 0.9 },
              }}
            />

          {/* Image count badge */}
          {imageCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                borderRadius: 999,
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.2)',
                zIndex: 4,
                pointerEvents: 'none'
              }}
            >
              <PhotoCamera sx={{ fontSize: 16, color: 'white' }} />
              <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 600 }}>
                {imageCount}
              </Typography>
            </Box>
          )}
          </Box>

          {/* Time Remaining Badge (bottom-left) */}
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 2 }}>
            <Chip
              icon={<Timer sx={{ fontSize: 18, color: 'white' }} />}
              label={formatTimeRemaining()}
              size="small"
              sx={{
                height: 30,
                px: 0.5,
                fontSize: '0.8rem',
                fontWeight: 900,
                letterSpacing: '0.2px',
                color: 'white',
                background: isEnded
                  ? 'linear-gradient(135deg, rgba(239,68,68,1) 0%, rgba(185,28,28,1) 100%)'
                  : 'linear-gradient(135deg, rgba(239,68,68,1) 0%, rgba(249,115,22,1) 100%)',
                boxShadow: '0 16px 34px rgba(239, 68, 68, 0.35)',
                border: '1px solid rgba(255,255,255,0.28)',
                backdropFilter: 'blur(10px)',
                '& .MuiChip-icon': { ml: 0.5 },
                '& .MuiChip-label': { px: 0.9 },
              }}
            />
          </Box>

          {/* Product Image */}
          <Box sx={{ 
            width: 200, 
            height: 200, 
            flexShrink: 0,
            mr: 3,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 1,
            bgcolor: '#f8f9fa'
          }}>
            <img
              src={product.image}
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.2s ease',
              }}
            />

            {/* Image count badge */}
            {imageCount > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 8,
                  bottom: 10,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  borderRadius: 999,
                  px: 1,
                  py: 0.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  zIndex: 3,
                  pointerEvents: 'none'
                }}
              >
                <PhotoCamera sx={{ fontSize: 16, color: 'white' }} />
                <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 600 }}>
                  {imageCount}
                </Typography>
              </Box>
            )}
            
            {/* Auction Progress Bar */}
            <Box sx={{ 
              position: 'absolute', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              height: 4,
              bgcolor: 'rgba(0,0,0,0.1)'
            }}>
              <LinearProgress
                variant="determinate"
                value={getProgressPercentage()}
                sx={{
                  height: '100%',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #ff6b35 0%, #f7931e 100%)',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Product Info */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Product Name */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                mb: 1,
                color: '#d84315',
                cursor: 'pointer',
                '&:hover': {
                  color: '#bf360c',
                  textDecoration: 'underline'
                },
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {product.name}
            </Typography>

            {/* Current Bid */}
            {showPrice && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp sx={{ color: '#ff6b35', fontSize: 16 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {t('auctionCard.currentBid')}
                </Typography>
                <Typography variant="h6" sx={{ color: '#ff6b35', fontWeight: 'bold' }}>
                  ${currentBid}
                </Typography>
              </Box>
            )}

            {/* Bid Count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
              {bidCount} {t('auctionCard.bids')}
            </Typography>

            {/* Reserve Price */}
            {hasReservePrice && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                {t('auctionCard.reserve')} ${reservePrice}
              </Typography>
            )}

            {/* Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Rating 
                  value={product.rating} 
                  precision={0.1} 
                  size="small" 
                  readOnly 
                  sx={{
                    '& .MuiRating-icon': {
                      fontSize: '1rem'
                    }
                  }}
                />
                <Typography variant="body2" sx={{ ml: 0.5, color: '#0066cc' }}>
                  {product.rating.toFixed(1)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                ({product.reviewCount.toLocaleString()})
              </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
              <Button
                variant="contained"
                size="medium"
                startIcon={isOwnProduct ? <Visibility /> : <Gavel />}
                onClick={isOwnProduct ? handleViewBids : handleBid}
                sx={{
                  background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  px: isMobile ? 3.25 : 3,
                  py: isMobile ? 1.15 : 1,
                  textTransform: 'none',
                  fontSize: isMobile ? '1rem' : '0.9rem',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e55a2b 0%, #e6851a 100%)',
                  },
                  boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                }}
              >
                {isOwnProduct
                  ? t('auctionCard.viewBids')
                  : (isEnded ? t('auctionCard.viewResults') : t('auctionCard.placeBid'))}
              </Button>
              
              <IconButton 
                onClick={handleToggleFavorite} 
                sx={{
                  color: isFavorite ? '#ff6b6b' : '#666',
                  width: isMobile ? 44 : undefined,
                  height: isMobile ? 44 : undefined,
                  '& svg': { fontSize: isMobile ? 26 : undefined },
                  '&:hover': {
                    bgcolor: 'rgba(255, 107, 107, 0.1)',
                  }
                }}
              >
                {isFavorite ? <Favorite /> : <FavoriteBorder />}
              </IconButton>

              <IconButton 
                onClick={handleShare}
                sx={{
                  color: '#666',
                  width: isMobile ? 44 : undefined,
                  height: isMobile ? 44 : undefined,
                  '& svg': { fontSize: isMobile ? 26 : undefined },
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                <Share />
              </IconButton>
            </Box>
          </Box>
        </Card>
      </motion.div>
        <BidPlacementPopup
          open={bidPopupOpen}
          onClose={() => setBidPopupOpen(false)}
          product={product}
          onPlaceBid={handlePlaceBid}
        />

        {product.auction?.id && (
          <BidPlacementHistoryPopup
            open={bidHistoryOpen}
            onClose={() => setBidHistoryOpen(false)}
            auctionId={product.auction.id}
            productName={product.name}
          />
        )}
      </>
    );
  }

  return (
    <>
      <motion.div
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          borderRadius: 1,
          boxShadow: 'none',
          border: '2px solid #ff6b35',
          background: 'linear-gradient(135deg, #fff5f0 0%, #ffffff 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(255, 107, 53, 0.2)',
            borderColor: '#ff5722',
          },
          transition: 'all 0.2s ease',
        }}
        onClick={handleViewDetails}
      >
        {/* Product Image */}
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height={variant === 'detailed' ? 380 : 340}
            image={product.image}
            alt={product.name}
            sx={{
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />

          {/* Image count badge */}
          {imageCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                borderRadius: 999,
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.2)',
                zIndex: 4,
                pointerEvents: 'none'
              }}
            >
              <PhotoCamera sx={{ fontSize: 16, color: 'white' }} />
              <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 600 }}>
                {imageCount}
              </Typography>
            </Box>
          )}
          
          {/* Auction Badge (top-left) */}
          <Chip
            icon={<Gavel sx={{ fontSize: 18, color: 'white' }} />}
            label={t('auctionCard.badge.auction')}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 4,
              height: 30,
              px: 0.5,
              fontSize: '0.8rem',
              fontWeight: 900,
              letterSpacing: '0.2px',
              color: 'white',
              background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
              boxShadow: '0 12px 26px rgba(15, 23, 42, 0.20)',
              border: '1px solid rgba(255,255,255,0.35)',
              backdropFilter: 'blur(10px)',
              '& .MuiChip-icon': { ml: 0.5 },
              '& .MuiChip-label': { px: 0.9 },
            }}
          />

          {/* My auction badge (stack under auction badge) */}
          {isOwnProduct && (
            <Chip
              label={t('auctionCard.badge.myAuction')}
              size="small"
              sx={{
                position: 'absolute',
                top: 50,
                left: 12,
                zIndex: 4,
                height: 26,
                px: 0.5,
                fontSize: '0.75rem',
                fontWeight: 900,
                letterSpacing: '0.2px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.25)',
                backdropFilter: 'blur(10px)',
                '& .MuiChip-label': { px: 0.85 },
              }}
            />
          )}

          {/* Countdown (bottom-left, red/orange) */}
          <Chip
            icon={<Timer sx={{ fontSize: 18, color: 'white' }} />}
            label={formatTimeRemaining()}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              zIndex: 4,
              height: 30,
              px: 0.5,
              fontSize: '0.8rem',
              fontWeight: 900,
              letterSpacing: '0.2px',
              color: 'white',
              background: isEnded
                ? 'linear-gradient(135deg, rgba(239,68,68,1) 0%, rgba(185,28,28,1) 100%)'
                : 'linear-gradient(135deg, rgba(239,68,68,1) 0%, rgba(249,115,22,1) 100%)',
              boxShadow: '0 16px 34px rgba(239, 68, 68, 0.35)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
              '& .MuiChip-icon': { ml: 0.5 },
              '& .MuiChip-label': { px: 0.9 },
            }}
          />

          {/* NEW badge (match the beautiful pill style) */}
          {product.isNew && (
            <Chip
              label={t('auctionCard.badge.new')}
              size="small"
              sx={{
                position: 'absolute',
                right: 12,
                bottom: imageCount > 0 ? 48 : 12,
                zIndex: 4,
                height: 30,
                px: 0.5,
                fontSize: '0.82rem',
                fontWeight: 900,
                letterSpacing: '0.2px',
                color: '#064e3b',
                background: 'linear-gradient(135deg, rgba(34,197,94,0.22) 0%, rgba(16,185,129,0.42) 100%)',
                boxShadow: '0 12px 28px rgba(16, 185, 129, 0.18)',
                border: '1px solid rgba(16,185,129,0.35)',
                backdropFilter: 'blur(10px)',
                '& .MuiChip-label': { px: 0.85 },
              }}
            />
          )}

          {/* Action Buttons */}
          <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Tooltip title={isFavorite ? t('auctionCard.tooltip.removeFromFavorites') : t('auctionCard.tooltip.addToFavorites')}>
              <IconButton
                onClick={handleToggleFavorite}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: isFavorite ? 'error.main' : 'text.secondary',
                  width: isMobile ? 46 : undefined,
                  height: isMobile ? 46 : undefined,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,1)',
                  },
                }}
                size={isMobile ? 'medium' : 'small'}
              >
                {isFavorite
                  ? <Favorite sx={{ fontSize: isMobile ? 24 : 20 }} />
                  : <FavoriteBorder sx={{ fontSize: isMobile ? 24 : 20 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('auctionCard.tooltip.share')}>
              <IconButton
                onClick={handleShare}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  width: isMobile ? 46 : undefined,
                  height: isMobile ? 46 : undefined,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,1)',
                  },
                }}
                size={isMobile ? 'medium' : 'small'}
              >
                <Share sx={{ fontSize: isMobile ? 24 : 20 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Auction Progress Bar */}
          <Box sx={{ 
            position: 'absolute', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            height: 6,
            bgcolor: 'rgba(0,0,0,0.1)'
          }}>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage()}
              sx={{
                height: '100%',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #ff6b35 0%, #f7931e 100%)',
                }
              }}
            />
          </Box>
        </Box>

        {/* Product Content */}
        <CardContent sx={{ flex: 1, px: 1.25, pt: 1, pb: 0.5, display: 'flex', flexDirection: 'column' }}>

          {/* Product Name */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: '0.95rem',
              lineHeight: 1.3,
              mb: 0.5,
              color: '#d84315',
              cursor: 'pointer',
              '&:hover': {
                color: '#bf360c',
                textDecoration: 'underline'
              },
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.name}
          </Typography>

          {/* Current Bid Section */}
          <Box sx={{ 
            bgcolor: '#fff3e0', 
            borderRadius: 1, 
            p: 1, 
            mb: 1,
            border: '1px solid #ffcc80'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <TrendingUp sx={{ color: '#ff6b35', fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {t('auctionCard.currentBid')}
              </Typography>
            </Box>
            {showPrice && (
              <Typography variant="h6" sx={{ color: '#ff6b35', fontWeight: 'bold', mb: 0.25 }}>
                ${currentBid}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {bidCount} {t('auctionCard.bids')}
              {hasReservePrice ? ` • ${t('auctionCard.reserve')} $${reservePrice}` : ''}
            </Typography>
          </Box>

          {/* Description (for detailed variant) */}
          {variant === 'detailed' && product.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 0.75,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
              }}
            >
              {product.description}
            </Typography>
          )}

          {/* Rating */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Rating 
                value={product.rating} 
                precision={0.1} 
                size="small" 
                readOnly 
                sx={{
                  '& .MuiRating-icon': {
                    fontSize: '0.9rem'
                  }
                }}
              />
              <Typography variant="body2" sx={{ ml: 0.5, color: '#0066cc' }}>
                {product.rating}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              ({product.reviewCount.toLocaleString()})
            </Typography>
          </Box>

          {/* Seller Info */}
          {showSeller && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Avatar
                src={product.seller.avatar}
                sx={{ width: 24, height: 24 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {product.seller.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn sx={{ fontSize: 12, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {product.seller.location || t('common.unknownLocation')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Category + Tags in one row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
            {showCategory && (
              <Chip
                label={categoryLabel}
                size="small"
                variant="outlined"
                sx={{ 
                  borderRadius: 999,
                  fontWeight: 700,
                  borderColor: 'rgba(255,107,53,0.35)',
                  color: '#ff6b35',
                  backgroundColor: 'rgba(255,107,53,0.06)'
                }}
              />
            )}
            {(product.tags || []).slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            ))}
          </Box>
        </CardContent>

        {/* Actions */}
        <CardActions sx={{ p: 1.25, pt: 0, flexDirection: 'column', gap: 0.5, mt: 'auto' }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={isOwnProduct ? <Visibility /> : <Gavel />}
            onClick={isOwnProduct ? handleViewBids : handleBid}
            sx={{
              background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
              borderRadius: 999,
              py: 1.2,
              fontWeight: 800,
              textTransform: 'none',
              boxShadow: '0 10px 18px rgba(255,96,0,0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e55a2b 0%, #e6851a 100%)',
              }
            }}
          >
            {isOwnProduct ? t('auctionCard.viewBids') : t('auctionCard.placeBid')}
          </Button>
        </CardActions>
      </Card>
      </motion.div>
      <BidPlacementPopup
        open={bidPopupOpen}
        onClose={() => setBidPopupOpen(false)}
        product={product}
        onPlaceBid={handlePlaceBid}
      />

      {product.auction?.id && (
        <BidPlacementHistoryPopup
          open={bidHistoryOpen}
          onClose={() => setBidHistoryOpen(false)}
          auctionId={product.auction.id}
          productName={product.name}
        />
      )}
    </>
  );
};

export default AuctionCard;
